import React, { useState, useEffect, useMemo } from "react";
import { useGetAdminPageDataQuery } from "state/api";
import { useTheme } from "@mui/material/styles";
import { DownloadOutlined } from "@mui/icons-material";
import { RingLoader } from 'react-spinners';
import { useNavigate } from "react-router-dom";
import {
  Grid, Card, CardContent, Typography, Button, TextField, Box,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from "@mui/material";
import * as XLSX from "xlsx";
import { useDispatch } from 'react-redux';
import { setRmMgName } from "state/authSlice";
import { List } from "@mui/icons-material";

const Admin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: rawResponse, isLoading, error } = useGetAdminPageDataQuery({});
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();

  useEffect(() => {
    if (rawResponse && Array.isArray(rawResponse)
    ) {
      // Extract the first array element which contains our data
      const responseData = rawResponse[0];
      if (Array.isArray(responseData)) {
        setFilteredData(responseData);
      }
    }
  }, [rawResponse]);

  console.log(filteredData)

  // Extract dynamic columns from data
  const dynamicColumns = useMemo(() => {
    if (filteredData.length === 0) return [];

    const columns = new Set();
    filteredData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'TeamName' && key.includes('_Approved')) {
          columns.add(key.replace('_Approved', ''));
        }
      });
    });



    return Array.from(columns).sort((a, b) => {
      // Extract date portion (format: DD-MM-YYYY)
      const dateStrA = a.split('(')[0];
      const dateStrB = b.split('(')[0];

      // Convert to YYYY-MM-DD format for proper date comparison
      const dateA = new Date(
        dateStrA.split('-')[2],
        dateStrA.split('-')[1] - 1,
        dateStrA.split('-')[0]
      );

      const dateB = new Date(
        dateStrB.split('-')[2],
        dateStrB.split('-')[1] - 1,
        dateStrB.split('-')[0]
      );

      return dateA - dateB;
    });
  }, [filteredData])
    ;

  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RM Daily Performance");
    XLSX.writeFile(workbook, "rm_daily_performance.xlsx");
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (rawResponse && rawResponse[0]) {
      const filtered = rawResponse[0].filter(rm =>
        rm.TeamName.toLowerCase().includes(term.toLowerCase())||
        rm.product.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredData(filtered);
    }
  };

  // Handle team name click
  const handleTeamClick = (teamName) => {
    dispatch(setRmMgName(teamName));
    navigate("/rm-details");
  };

  const CustomSpinner = () => (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: theme.palette.background.default
    }}>
      <RingLoader color={theme.palette.primary.main} size={100} />
    </Box>
  );

  if (isLoading) return <CustomSpinner />;
  if (error) return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: 2,
      textAlign: 'center',
      padding: 3
    }}>
      <Typography variant="h5" color="error">
        Error loading data
      </Typography>
      <Typography variant="body1">
        {error.message}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={() => window.location.reload()}
      >
        Try Again
      </Button>
    </Box>
  );

  return (
    <Box sx={{
      padding: { xs: 2, md: 4 },
      maxWidth: '1440px',
      margin: '0 auto',
      minHeight: '100vh',
      background: theme.palette.background.default
    }}>
      {/* Header Section */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 3,
        mb: 4,
        padding: 3,
        borderRadius: 4,
        background: `linear-gradient(145deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
        boxShadow: 3
      }}>
        <TextField
          label="Search Team"
          variant="filled"
          value={searchTerm}
          onChange={handleSearch}
          sx={{
            flexGrow: 1,
            width: '100%',
            '& .MuiFilledInput-root': {
              background: theme.palette.background.paper,
              borderRadius: 2,
              '&:before': { borderBottom: 'none' },
              '&:hover': { background: theme.palette.background.paper }
            }
          }}
          InputProps={{
            disableUnderline: true,
          }}
        />

        <Box sx={{
          display: 'flex',
          gap: 2,
          width: { xs: '100%', sm: 'auto' },
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Button
            onClick={() => navigate('/Dailywise')}
            variant="contained"
            startIcon={<List />}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              fontWeight: 'bold',
              textTransform: 'none',
              background: theme.palette.success.main,
              color: theme.palette.getContrastText(theme.palette.success.main),
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 4,
                background: theme.palette.success.dark,
              },
              transition: 'all 0.3s ease',
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Rx Score
          </Button>
          <Button
            onClick={() => navigate('/DailyWisePob')}
            variant="contained"
            startIcon={<List />}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              fontWeight: 'bold',
              textTransform: 'none',
              background: theme.palette.success.main,
              color: theme.palette.getContrastText(theme.palette.success.main),
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 4,
                background: theme.palette.success.dark,
              },
              transition: 'all 0.3s ease',
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Pob Score
          </Button>

          <Button
            onClick={() => navigate('/imagePreviewModal')}
            variant="contained"
            startIcon={<List />}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              fontWeight: 'bold',
              textTransform: 'none',
              background: theme.palette.info.main,
              color: theme.palette.getContrastText(theme.palette.info.main),
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 4,
                background: theme.palette.info.dark,
              },
              transition: 'all 0.3s ease',
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Prescription
          </Button>
          <Button
            onClick={() => navigate('/pobImagePreviewModal')}
            variant="contained"
            startIcon={<List />}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              fontWeight: 'bold',
              textTransform: 'none',
              background: theme.palette.info.main,
              color: theme.palette.getContrastText(theme.palette.info.main),
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 4,
                background: theme.palette.info.dark,
              },
              transition: 'all 0.3s ease',
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Invoice
          </Button>

          <Button
            onClick={downloadExcel}
            variant="contained"
            startIcon={<DownloadOutlined />}
            sx={{
              py: 1,
              px: 2.5,
              borderRadius: 2,
              fontWeight: 'bold',
              textTransform: 'none',
              background: theme.palette.secondary.main,
              color: theme.palette.getContrastText(theme.palette.secondary.main),
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 4,
                background: theme.palette.secondary.dark,
              },
              transition: 'all 0.3s ease',
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      {/* Data Table */}
      {/* Data Table */}
      {filteredData.length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 4,
            boxShadow: 3,
            maxHeight: '60vh',
            overflow: 'auto',
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Table stickyHeader>

            {/* 🔹 CASE 1: OLD LOGIC (Approved/Rejected/Pending) */}
            {dynamicColumns.length > 0 ? (
              <>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Team Name</TableCell>
                    {dynamicColumns.map(col => (
                      <TableCell key={col} colSpan={3} align="center">
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredData.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.TeamName}</TableCell>
                      {dynamicColumns.map(col => (
                        <React.Fragment key={col}>
                          <TableCell>{row[`${col}_Approved`] || 0}</TableCell>
                          <TableCell>{row[`${col}_Rejected`] || 0}</TableCell>
                          <TableCell>{row[`${col}_Pending`] || 0}</TableCell>
                        </React.Fragment>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </>
            ) : (
              /* 🔹 CASE 2: NEW API RESPONSE (SMALL FALLBACK TABLE) */
              <>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Team Name</TableCell>
                    <TableCell align="center">Product</TableCell>
                    <TableCell align="center">Today's Prescription</TableCell>
                    <TableCell align="center">Current Week Prescription</TableCell>
                    <TableCell align="center">Today's POB</TableCell>
                    <TableCell align="center">Today's POB Strips</TableCell>
                    <TableCell align="center">Current Week POB</TableCell>
                    <TableCell align="center">Current Week POB Strips</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredData.map((row, idx) => (
                    <TableRow key={idx} hover>
                      {/* <TableCell
                        onClick={() => handleTeamClick(row.TeamName)}
                        sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                      >
                        {row.TeamName || "UNKNOWN TEAM"}
                      </TableCell> */}
                      <TableCell align="center">{row.TeamName}</TableCell>
                      <TableCell align="center">{row.product}</TableCell>
                      <TableCell align="center">{row.TodayPrescription}</TableCell>
                      <TableCell align="center">{row.CurrentWeekPrescription}</TableCell>
                      <TableCell align="center">{row.TodayPOB}</TableCell>
                      <TableCell align="center">{row.Today_No_of_Strips}</TableCell>
                      <TableCell align="center">{row.CurrentWeekPOB}</TableCell>
                      <TableCell align="center">{row.CurrentWeek_No_of_Strips}</TableCell>

                    </TableRow>
                  ))}
                </TableBody>
              </>
            )}
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography>No data available</Typography>
        </Box>
      )}
      {/* : (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        textAlign: 'center',
        padding: 4,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        background: theme.palette.background.paper
      }}>
        <Typography variant="h6" color="textSecondary">
          {filteredData.length === 0 && !isLoading
            ? "No data available"
            : "Loading data..."}
        </Typography>
      </Box>
      )} */}
    </Box>
  );
};

export default Admin;